import { memo } from 'react';
import { useTerminalStore, selectWatchlistTokens } from '../../stores/terminal.store.js';
import { formatPriceWithSymbol, formatChange } from '@arc/shared';
import { TokenSearch } from './TokenSearch.js';

// ─── Section Label ─────────────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 mb-2">
      <span
        className="font-orb text-[11px] tracking-[3px] flex-shrink-0"
        style={{ color: 'var(--color-muted)' }}
      >
        {label}
      </span>
      <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
    </div>
  );
}

// ─── Mini Sparkline ────────────────────────────────────────────────────────────

function MiniSpark({ isUp }: { isUp: boolean }) {
  // 10 pseudo-random bar heights for visual texture
  const bars = [40, 55, 35, 70, 45, 80, 50, 65, isUp ? 85 : 30, isUp ? 95 : 20];
  const color = isUp ? 'var(--color-buy)' : 'var(--color-sell)';
  return (
    <div className="flex items-end gap-px mt-1.5" style={{ height: '18px' }}>
      {bars.map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm"
          style={{
            height: `${h}%`,
            background: color,
            opacity: 0.3 + (i / bars.length) * 0.5,
          }}
        />
      ))}
    </div>
  );
}

// ─── Token Card ───────────────────────────────────────────────────────────────

interface TokenCardProps {
  symbol:   string;
  name:     string;
  price:    number;
  change:   number;
  isActive: boolean;
  isCustom: boolean;
  onClick:  () => void;
  onRemove: (() => void) | undefined;
}

const TokenCard = memo(function TokenCard({
  symbol, name, price, change, isActive, isCustom, onClick, onRemove,
}: TokenCardProps) {
  const hasData = price > 0;
  const isUp = change >= 0;
  const changeColor = !hasData || change === 0 ? 'var(--color-text)' : isUp ? 'var(--color-buy)' : 'var(--color-sell)';

  return (
    <div
      className="relative group cursor-pointer transition-all duration-200"
      style={{
        background: isActive ? 'rgba(0,31,53,0.9)' : 'var(--color-panel)',
        border: `1px solid ${isActive ? 'rgba(0,212,255,0.4)' : 'var(--color-border)'}`,
        clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)',
        marginBottom: '6px',
        padding: '10px',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      {/* Left accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 transition-all duration-200"
        style={{
          width: '2px',
          background: isActive ? 'var(--color-accent)' : 'var(--color-arc)',
          boxShadow: isActive
            ? '0 0 8px var(--color-accent)'
            : '0 0 8px var(--color-arc)',
          opacity: isActive ? 1 : 0,
        }}
      />
      {/* Hover left bar via CSS group */}
      <div
        className="absolute left-0 top-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        style={{
          width: '2px',
          background: 'var(--color-arc)',
          boxShadow: '0 0 8px var(--color-arc)',
          display: isActive ? 'none' : 'block',
        }}
      />

      {/* Header row: symbol + change% */}
      <div className="flex flex-wrap justify-between items-center gap-x-1 mb-1 min-w-0">
        <span className="font-orb font-bold text-[12px] whitespace-nowrap" style={{ color: 'var(--color-bright)' }}>
          {symbol}
          {isCustom && (
            <span
              className="ml-1 text-[11px] font-mono px-0.5 leading-tight"
              style={{ color: 'var(--color-accent)', border: '1px solid rgba(255,215,0,0.4)' }}
            >
              CUSTOM
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="font-mono text-[11px] font-bold"
            style={{ color: changeColor }}
          >
            {!hasData ? '—' : <>{change === 0 ? '' : isUp ? '▲ ' : '▼ '}{formatChange(change)}</>}
          </span>
          {onRemove && (
            <button
              onClick={e => { e.stopPropagation(); onRemove(); }}
              className="text-[11px] leading-none px-0.5 opacity-60 hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              style={{ color: 'var(--color-sell)' }}
              title="Remove from watchlist"
              aria-label={`Remove ${symbol} from watchlist`}
            >
              ✕
            </button>
          )}
        </span>
      </div>

      {/* Price */}
      <div
        className="font-mono text-[13px] mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis" title={price > 0 ? formatPriceWithSymbol(price) : undefined}
        style={{ color: 'var(--color-arc)' }}
      >
        {price > 0 ? formatPriceWithSymbol(price) : '---'}
      </div>

      {/* Name */}
      <div
        className="font-mono text-[11px] tracking-[1px]"
        style={{ color: 'var(--color-muted)' }}
      >
        {name}
      </div>

      {/* Mini sparkline */}
      {hasData ? <MiniSpark isUp={isUp} /> : (
        <div className="font-mono text-[11px] mt-1" style={{ color: 'var(--color-text)' }} role="status">NO PRICE FEED</div>
      )}

    </div>
  );
});

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export const Sidebar = memo(function Sidebar() {
  const tokens       = useTerminalStore(selectWatchlistTokens);
  const prices       = useTerminalStore(s => s.prices);
  const activePanels = useTerminalStore(s => s.activePanels);
  const focusedIdx   = useTerminalStore(s => s.focusedPanelIndex);
  const settings     = useTerminalStore(s => s.settings);
  const setActive    = useTerminalStore(s => s.setActiveSymbol);
  const removeToken  = useTerminalStore(s => s.removeToken);
  const unwatch      = useTerminalStore(s => s.removeFromWatchlist);

  // The chart watchlist clicks load into; its symbol is the highlighted row
  // (its UT Bot settings live in the signal panel's CONFIG tab).
  const activePanel  = activePanels[focusedIdx] ?? activePanels[0];
  const customSet    = new Set((settings.customTokens ?? []).map(t => t.symbol));

  return (
    <div className="h-full flex flex-col">
      {/* Token search */}
      <div className="flex-shrink-0 px-3 pt-2 pb-1">
        <TokenSearch />
      </div>

      {/* Section label */}
      <SectionLabel label="WATCHLIST" />

      {/* Token list */}
      <div className="flex-1 overflow-y-auto px-3 min-h-0">
        {tokens.map(token => {
          const p = prices[token.symbol];
          return (
            <TokenCard
              key={token.symbol}
              symbol={token.symbol}
              name={token.name}
              price={p?.priceUsd ?? 0}
              change={p?.change24h ?? 0}
              isActive={token.symbol === activePanel?.symbol}
              isCustom={customSet.has(token.symbol)}
              onClick={() => setActive(token.symbol)}
              // Custom tokens are deleted; built-in ones just leave the watchlist
              // (still selectable on charts, re-added by searching for them).
              onRemove={() => customSet.has(token.symbol) ? removeToken(token.symbol) : unwatch(token.symbol)}
            />
          );
        })}
        {tokens.length === 0 && (
          <div className="font-mono text-[11px] tracking-wider py-4 text-center" style={{ color: 'var(--color-muted)' }} role="status">
            WATCHLIST EMPTY — SEARCH ABOVE TO ADD TOKENS
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Settings badge (used in DynamicGrid PanelShell) ─────────────────────────

export function SidebarSettingsBadge() {
  const setShowAlerts = useTerminalStore(s => s.setShowAlertSettings);
  return (
    <button
      onMouseDown={e => e.stopPropagation()}
      onClick={() => setShowAlerts(true)}
      title="Alert Settings"
      aria-label="Open alert settings"
      className="font-mono text-[11px] leading-none transition-colors ml-1"
      style={{ color: 'var(--color-bright)' }}
      onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-arc)')}
      onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-bright)')}
    >
      ⚙
    </button>
  );
}
